/*
 * Загрузка ресурсов платформы для главной: кластеры (каталог + приложения ArgoCD),
 * модели (каталог + ArgoCD + чемпион из MLflow), сервисы (каталог + ArgoCD по
 * окружениям), базы PostgreSQL (каталог + ArgoCD + список баз из IaC — CR CloudNativePG
 * через API кластера, размеры из Prometheus) и брокеры Kafka (каталог + ArgoCD +
 * Prometheus: топики и брокеры). Один вызов на страницу — результат делят сводные
 * плитки и все таблицы.
 */
import { Entity } from '@backstage/catalog-model';
import {
  configApiRef,
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { kubernetesApiRef } from '@backstage/plugin-kubernetes';
import { argoCDApiRef } from '@roadiehq/backstage-plugin-argo-cd';
import { mlflowApiRef } from '@internal/plugin-mlflow';
import useAsync, { AsyncState } from 'react-use/esm/useAsync';
import { PromAlert, fetchAlerts } from '../../lib/alerts';
import {
  AIRFLOW_TYPE,
  BROKER_TYPE,
  CLUSTER_ENV_ANNOTATION,
  DATABASE_TYPE,
  DATASTORE_CLUSTER_ANNOTATION,
  DEFECTDOJO_TYPE,
  KUBERNETES_CLUSTER_TYPE,
  MLFLOW_MODEL_NAME_ANNOTATION,
  ML_MODEL_TYPE,
  SERVICE_TYPE,
  SERVING_ENVS,
  annotation,
  compareByName,
  datastoreNameOf,
  envRank,
  servingEnvOf,
} from '../../lib/viva';
import {
  CnpgCluster,
  CnpgDatabase,
  DATASTORES_NAMESPACE,
  pgDatabases,
} from '../../lib/datastores';
import { CLUSTER_NOT_CONNECTED, clusterClient } from '../../lib/k8sProxy';
import {
  PromSample,
  kafkaBrokersQuery,
  kafkaTopicsQuery,
  postgresDatabasesQuery,
  queryInstant,
} from '../../lib/prometheus';
import { ArgoApp, argoAppSelector, fetchArgoApps } from '../argocd';
import { CellState, ModelSummary, settle } from '../catalog/cells';

export type ClusterRow = {
  entity: Entity;
  apps: CellState<ArgoApp[]>;
  /** Активные алерты Prometheus кластера (плитка «Алерты» на главной). */
  alerts: CellState<PromAlert[]>;
};

export type ModelRow = {
  entity: Entity;
  apps: CellState<ArgoApp[]>;
  summary: CellState<ModelSummary | undefined>;
};

export type ServiceRow = { entity: Entity; apps: CellState<ArgoApp[]> };

/** База из IaC; размер — из Prometheus, если он доступен; applied — статус CR Database. */
export type HomeDatabase = { name: string; bytes?: number; applied?: boolean };

export type DatabaseRow = {
  entity: Entity;
  apps: CellState<ArgoApp[]>;
  /** Базы, описанные в IaC (initdb кластера + CR Database); ошибка — API кластера недоступен. */
  databases: CellState<HomeDatabase[]>;
};

export type BrokerRow = {
  entity: Entity;
  apps: CellState<ArgoApp[]>;
  kafka: CellState<{ topics: number; brokers: number }>;
};

export type Resources = {
  clusters: ClusterRow[];
  models: ModelRow[];
  services: ServiceRow[];
  databases: DatabaseRow[];
  brokers: BrokerRow[];
  /** Инстансы Airflow — только для привязки алертов, таблицы на главной у них нет. */
  airflow: Entity[];
  /** DefectDojo кластеров — тоже только для привязки алертов. */
  defectdojo: Entity[];
};

const byEnvThenName = (a: ClusterRow, b: ClusterRow) =>
  envRank(annotation(a.entity, CLUSTER_ENV_ANNOTATION)) -
    envRank(annotation(b.entity, CLUSTER_ENV_ANNOTATION)) ||
  compareByName(a.entity, b.entity);

/** 0 — есть production, 1 — только staging, 2 — что-то ещё развёрнуто, 3 — ничего. */
export const deployRank = ({
  entity,
  apps,
}: {
  entity: Entity;
  apps: CellState<ArgoApp[]>;
}): number => {
  const names = (apps.value ?? []).map(app => app.name);
  if (names.length === 0) return SERVING_ENVS.length + 1;
  const envs = names
    .map(name => servingEnvOf(name, entity.metadata.name))
    .filter((env): env is (typeof SERVING_ENVS)[number] => Boolean(env));
  return envs.length
    ? Math.min(...envs.map(env => SERVING_ENVS.indexOf(env)))
    : SERVING_ENVS.length;
};

const byDeployedFirst = <T extends ModelRow | ServiceRow>(a: T, b: T) =>
  deployRank(a) - deployRank(b) || compareByName(a.entity, b.entity);

export function useResources(): AsyncState<Resources> {
  const catalogApi = useApi(catalogApiRef);
  const argoApi = useApi(argoCDApiRef);
  const mlflowApi = useApi(mlflowApiRef);
  const config = useApi(configApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const kubernetesApi = useApi(kubernetesApiRef);

  return useAsync(async (): Promise<Resources> => {
    const appsOf = (entity: Entity) => {
      const selector = argoAppSelector(entity);
      return settle(
        selector
          ? fetchArgoApps(argoApi, config, selector)
          : Promise.resolve([] as ArgoApp[]),
      );
    };

    // Prometheus кластера, к которому привязано хранилище; без привязки — данных нет
    const prom = (entity: Entity, query: (name: string) => string) => {
      const cluster = annotation(entity, DATASTORE_CLUSTER_ANNOTATION);
      return settle<PromSample[]>(
        cluster
          ? queryInstant(
              { discoveryApi, fetchApi },
              cluster,
              query(datastoreNameOf(entity)),
            )
          : Promise.reject(new Error('нет привязки к кластеру')),
      );
    };

    // CR CloudNativePG namespace datastores — один запрос на Kubernetes-кластер на все его базы
    type Cnpg = { clusters: CnpgCluster[]; databases: CnpgDatabase[] };
    const cnpgByCluster = new Map<string, Promise<Cnpg>>();
    const cnpg = (entity: Entity): Promise<Cnpg> => {
      const cluster = annotation(entity, DATASTORE_CLUSTER_ANNOTATION);
      if (!cluster) return Promise.reject(new Error('нет привязки к кластеру'));
      let pending = cnpgByCluster.get(cluster);
      if (!pending) {
        pending = (async () => {
          const get = await clusterClient(kubernetesApi, cluster);
          if (!get) throw new Error(CLUSTER_NOT_CONNECTED);
          const base = `/apis/postgresql.cnpg.io/v1/namespaces/${DATASTORES_NAMESPACE}`;
          const [clusters, databases] = await Promise.all([
            get<{ items: CnpgCluster[] }>(`${base}/clusters`),
            get<{ items: CnpgDatabase[] }>(`${base}/databases`).catch(
              (): { items: CnpgDatabase[] } => ({ items: [] }),
            ),
          ]);
          return { clusters: clusters.items, databases: databases.items };
        })();
        cnpgByCluster.set(cluster, pending);
      }
      return pending;
    };

    const list = (kind: string, type: string) =>
      catalogApi.getEntities({ filter: { kind, 'spec.type': type } });

    const [
      clusterEntities,
      modelEntities,
      serviceEntities,
      databaseEntities,
      brokerEntities,
      airflowEntities,
      defectdojoEntities,
    ] = await Promise.all([
      list('Resource', KUBERNETES_CLUSTER_TYPE),
      list('Component', ML_MODEL_TYPE),
      list('Component', SERVICE_TYPE),
      list('Resource', DATABASE_TYPE),
      list('Resource', BROKER_TYPE),
      list('Component', AIRFLOW_TYPE),
      list('Component', DEFECTDOJO_TYPE),
    ]);

    const [clusters, models, services, databases, brokers] = await Promise.all([
      Promise.all(
        clusterEntities.items.map(async (entity): Promise<ClusterRow> => {
          const [apps, alerts] = await Promise.all([
            appsOf(entity),
            settle(
              fetchAlerts({ discoveryApi, fetchApi }, entity.metadata.name),
            ),
          ]);
          return { entity, apps, alerts };
        }),
      ),
      Promise.all(
        modelEntities.items.map(async (entity): Promise<ModelRow> => {
          const modelName = annotation(entity, MLFLOW_MODEL_NAME_ANNOTATION);
          // MLflow кластера модели; без аннотации — mlflow.baseUrl
          const cluster = annotation(entity, DATASTORE_CLUSTER_ANNOTATION);
          const [apps, summary] = await Promise.all([
            appsOf(entity),
            settle(
              modelName
                ? mlflowApi.getModelSummary(modelName, cluster)
                : Promise.resolve(undefined),
            ),
          ]);
          return { entity, apps, summary };
        }),
      ),
      Promise.all(
        serviceEntities.items.map(
          async (entity): Promise<ServiceRow> => ({
            entity,
            apps: await appsOf(entity),
          }),
        ),
      ),
      Promise.all(
        databaseEntities.items.map(async (entity): Promise<DatabaseRow> => {
          const name = datastoreNameOf(entity);
          const [apps, iac, samples] = await Promise.all([
            appsOf(entity),
            settle(cnpg(entity)),
            prom(entity, postgresDatabasesQuery),
          ]);
          const sizes = new Map(
            (samples.value ?? []).map(s => [s.metric.datname, s.value]),
          );
          const dbList: CellState<HomeDatabase[]> = iac.error
            ? { loading: false, error: iac.error }
            : {
                loading: false,
                value: pgDatabases(
                  name,
                  iac.value?.clusters.find(c => c.metadata.name === name),
                  iac.value?.databases ?? [],
                ).map(db => ({
                  name: db.name,
                  bytes: sizes.get(db.name),
                  applied: db.source === 'database' ? db.applied : undefined,
                })),
              };
          return { entity, apps, databases: dbList };
        }),
      ),
      Promise.all(
        brokerEntities.items.map(async (entity): Promise<BrokerRow> => {
          const [apps, topics, brokersUp] = await Promise.all([
            appsOf(entity),
            prom(entity, kafkaTopicsQuery),
            prom(entity, kafkaBrokersQuery),
          ]);
          const error = topics.error ?? brokersUp.error;
          const kafka: CellState<{ topics: number; brokers: number }> = error
            ? { loading: false, error }
            : {
                loading: false,
                value: {
                  topics: topics.value?.[0]?.value ?? 0,
                  brokers: brokersUp.value?.[0]?.value ?? 0,
                },
              };
          return { entity, apps, kafka };
        }),
      ),
    ]);

    return {
      clusters: clusters.sort(byEnvThenName),
      models: models.sort(byDeployedFirst),
      services: services.sort(byDeployedFirst),
      databases: databases.sort((a, b) => compareByName(a.entity, b.entity)),
      brokers: brokers.sort((a, b) => compareByName(a.entity, b.entity)),
      airflow: airflowEntities.items,
      defectdojo: defectdojoEntities.items,
    };
  }, [
    catalogApi,
    argoApi,
    mlflowApi,
    config,
    discoveryApi,
    fetchApi,
    kubernetesApi,
  ]);
}
